/* =====================================================
   MULTIVERSE BONDS
   Character-First Creator — STEP 3
   New backend connection + quota + persistence + result.
   No product/cart logic yet.
===================================================== */

(function () {
  'use strict';

  const DB_NAME =
    'multiverse-bonds-character-creator-storage';

  const DB_VERSION =
    1;

  const STORE_NAME =
    'creator-sessions';

  const MAX_FILE_BYTES =
    12 * 1024 * 1024;

  const GENERATION_TIMEOUT_MS =
    360000;


  function openDatabase() {
    return new Promise(
      (resolve, reject) => {
        if (!('indexedDB' in window)) {
          reject(
            new Error(
              'IndexedDB is not supported in this browser.'
            )
          );
          return;
        }

        const request =
          indexedDB.open(
            DB_NAME,
            DB_VERSION
          );

        request.onupgradeneeded =
          event => {
            const database =
              event.target.result;

            if (
              !database.objectStoreNames.contains(
                STORE_NAME
              )
            ) {
              database.createObjectStore(
                STORE_NAME,
                {
                  keyPath:
                    'key'
                }
              );
            }
          };

        request.onsuccess =
          () => {
            resolve(
              request.result
            );
          };

        request.onerror =
          () => {
            reject(
              request.error ||
              new Error(
                'Unable to open creator storage.'
              )
            );
          };
      }
    );
  }


  async function saveSession(
    session
  ) {
    const database =
      await openDatabase();

    return new Promise(
      (resolve, reject) => {
        const transaction =
          database.transaction(
            STORE_NAME,
            'readwrite'
          );

        transaction
          .objectStore(
            STORE_NAME
          )
          .put(
            session
          );

        transaction.oncomplete =
          () => {
            database.close();
            resolve();
          };

        transaction.onerror =
          () => {
            const error =
              transaction.error ||
              new Error(
                'Unable to save your Multiverse.'
              );

            database.close();
            reject(error);
          };
      }
    );
  }


  async function getSession(
    key
  ) {
    const database =
      await openDatabase();

    return new Promise(
      (resolve, reject) => {
        const transaction =
          database.transaction(
            STORE_NAME,
            'readonly'
          );

        const request =
          transaction
            .objectStore(
              STORE_NAME
            )
            .get(
              key
            );

        request.onsuccess =
          () => {
            database.close();

            resolve(
              request.result ||
              null
            );
          };

        request.onerror =
          () => {
            const error =
              request.error ||
              new Error(
                'Unable to restore your Multiverse.'
              );

            database.close();
            reject(error);
          };
      }
    );
  }


  async function deleteSession(
    key
  ) {
    const database =
      await openDatabase();

    return new Promise(
      (resolve, reject) => {
        const transaction =
          database.transaction(
            STORE_NAME,
            'readwrite'
          );

        transaction
          .objectStore(
            STORE_NAME
          )
          .delete(
            key
          );

        transaction.oncomplete =
          () => {
            database.close();
            resolve();
          };

        transaction.onerror =
          () => {
            const error =
              transaction.error ||
              new Error(
                'Unable to clear your saved Multiverse.'
              );

            database.close();
            reject(error);
          };
      }
    );
  }


  function initAll(
    root = document
  ) {
    root
      .querySelectorAll(
        '[data-multiverse-creator]'
      )
      .forEach(
        initCreator
      );
  }


  function initCreator(
    section
  ) {
    if (
      section.dataset.mvInitialized ===
      'true'
    ) {
      return;
    }

    section.dataset.mvInitialized =
      'true';


    /* -----------------------------
       CONFIG
    ------------------------------ */

    const apiBaseUrl =
      (
        section.dataset.apiBaseUrl ||
        ''
      )
        .trim()
        .replace(
          /\/+$/,
          ''
        );

    const generatePath =
      section.dataset.generatePath ||
      '/api/multiverse/jobs';

    const quotaPath =
      section.dataset.quotaPath ||
      '/api/multiverse/quota';

    const productionPath =
      section.dataset.productionPath ||
      '/api/multiverse/production/prepare';

    const customerId =
      (
        section.dataset.customerId ||
        ''
      ).trim();

    const cartAddUrl =
      (
        section.dataset.cartAddUrl ||
        '/cart/add.js'
      ).trim();

    const shopRoot =
      (
        section.dataset.shopRoot ||
        '/'
      ).trim();

    const shopCurrency =
      (
        section.dataset.shopCurrency ||
        'USD'
      ).trim();

    const storageKey =
      `multiverse-creator-current:${window.location.pathname}`;


    /* -----------------------------
       ELEMENTS
    ------------------------------ */

    const createStep =
      section.querySelector(
        '[data-mv-step="create"]'
      );

    const resultStep =
      section.querySelector(
        '[data-mv-step="result"]'
      );

    const form =
      section.querySelector(
        '[data-mv-form]'
      );

    const photoInput =
      section.querySelector(
        '[data-mv-photo]'
      );

    const uploadTitle =
      section.querySelector(
        '[data-mv-upload-title]'
      );

    const uploadPreview =
      section.querySelector(
        '[data-mv-upload-preview]'
      );

    const uploadThumb =
      section.querySelector(
        '[data-mv-upload-thumb]'
      );

    const generatedPreview =
      section.querySelector(
        '[data-mv-generated-preview]'
      );

    const generatedThumb =
      section.querySelector(
        '[data-mv-generated-thumb]'
      );

    const loadingOverlay =
      section.querySelector(
        '[data-mv-loading]'
      );

    const miniLoading =
      section.querySelector(
        '[data-mv-mini-loading]'
      );

    const createButton =
      section.querySelector(
        '[data-mv-create]'
      );

    const createButtonText =
      section.querySelector(
        '[data-mv-create-text]'
      );

    const message =
      section.querySelector(
        '[data-mv-message]'
      );

    const quotaBox =
      section.querySelector(
        '[data-mv-quota]'
      );

    const quotaTitle =
      section.querySelector(
        '[data-mv-quota-title]'
      );

    const quotaSubtitle =
      section.querySelector(
        '[data-mv-quota-subtitle]'
      );

    const quotaProgress =
      section.querySelector(
        '[data-mv-quota-progress]'
      );

    const backButton =
      section.querySelector(
        '[data-mv-back]'
      );

    const generateAnotherButton =
      section.querySelector(
        '[data-mv-generate-another]'
      );

    const resultImage =
      section.querySelector(
        '[data-mv-result-image]'
      );

    const resultArtThumb =
      section.querySelector(
        '[data-mv-result-art-thumb]'
      );

    const resultOriginalThumb =
      section.querySelector(
        '[data-mv-result-original-thumb]'
      );

    const resultViewButtons =
      section.querySelectorAll(
        '[data-mv-result-view]'
      );

    const resultVibe =
      section.querySelector(
        '[data-mv-result-vibe]'
      );

    const resultGenerationId =
      section.querySelector(
        '[data-mv-generation-id]'
      );

    const jobProgressBox = section.querySelector('[data-mv-job-progress]');
    const jobProgressText = section.querySelector('[data-mv-job-text]');
    const jobProgressCount = section.querySelector('[data-mv-job-count]');
    const jobProgressBar = section.querySelector('[data-mv-job-progress-bar]');
    const jobProgressSpinner = section.querySelector('[data-mv-job-spinner]');

    const chooseProductsButton =
      section.querySelector(
        '[data-mv-choose-products]'
      );

    const productsArea =
      section.querySelector(
        '[data-mv-products]'
      );

    const productCards =
      Array.from(
        section.querySelectorAll(
          '[data-mv-product-card]'
        )
      );

    const bundleCards =
      Array.from(
        section.querySelectorAll(
          '[data-mv-bundle]'
        )
      );

    const selectionBar =
      section.querySelector(
        '[data-mv-selection-bar]'
      );

    const addSelectedButton =
      section.querySelector(
        '[data-mv-add-selected]'
      );

    const addSelectedText =
      section.querySelector(
        '[data-mv-add-selected-text]'
      );

    const selectedCount =
      section.querySelector(
        '[data-mv-selected-count]'
      );

    const selectedTotal =
      section.querySelector(
        '[data-mv-selected-total]'
      );

    const cartMessage =
      section.querySelector(
        '[data-mv-cart-message]'
      );


    /* -----------------------------
       LOCAL STATE
    ------------------------------ */

    let originalPhotoBlob =
      null;

    let originalPhotoName =
      '';

    let originalObjectUrl =
      '';

    let generatedArtworkBlob =
      null;

    let generatedArtworkUrl =
      '';

    let generatedObjectUrl =
      '';

    let generationId =
      '';

    let currentVibe =
      'Romantic';

    let quotaState =
      null;

    let generationInFlight =
      false;

    let generatedProductMockups =
      [];

    const pendingJobKey = `multiverse-creator-job:${window.location.pathname}`;
    let pollSequence = 0;
    let currentJobId = '';
    let hasShownMaster = false;
    let lastPreviewCount = -1;



    /* -----------------------------
       OBJECT URL HELPERS
    ------------------------------ */

    function revokeUrl(
      url
    ) {
      if (
        url &&
        url.startsWith(
          'blob:'
        )
      ) {
        URL.revokeObjectURL(
          url
        );
      }
    }


    function setOriginalBlob(
      blob,
      name = ''
    ) {
      originalPhotoBlob =
        blob;

      originalPhotoName =
        name ||
        originalPhotoName ||
        'multiverse-photo.png';

      revokeUrl(
        originalObjectUrl
      );

      originalObjectUrl =
        blob
          ? URL.createObjectURL(
              blob
            )
          : '';

      if (
        uploadPreview &&
        originalObjectUrl
      ) {
        uploadPreview.src =
          originalObjectUrl;

        uploadPreview.hidden =
          false;
      }

      if (
        uploadThumb &&
        originalObjectUrl
      ) {
        uploadThumb.src =
          originalObjectUrl;

        uploadThumb.hidden =
          false;
      }

      if (
        resultOriginalThumb &&
        originalObjectUrl
      ) {
        resultOriginalThumb.src =
          originalObjectUrl;
      }

      if (
        uploadTitle &&
        originalPhotoName
      ) {
        uploadTitle.textContent =
          originalPhotoName;
      }
    }


    function setGeneratedBlob(
      blob,
      fallbackUrl = ''
    ) {
      generatedArtworkBlob =
        blob ||
        null;

      generatedArtworkUrl =
        fallbackUrl ||
        generatedArtworkUrl ||
        '';

      revokeUrl(
        generatedObjectUrl
      );

      generatedObjectUrl =
        blob
          ? URL.createObjectURL(
              blob
            )
          : generatedArtworkUrl;

      if (
        generatedPreview &&
        generatedObjectUrl
      ) {
        generatedPreview.src =
          generatedObjectUrl;

        generatedPreview.hidden =
          false;
      }

      if (
        generatedThumb &&
        generatedObjectUrl
      ) {
        generatedThumb.src =
          generatedObjectUrl;

        generatedThumb.hidden =
          false;
      }

      if (
        resultArtThumb &&
        generatedObjectUrl
      ) {
        resultArtThumb.src =
          generatedObjectUrl;
      }

      renderProductArtwork();
    }


    /* -----------------------------
       VIEW HELPERS
    ------------------------------ */

    function showCreateStep() {
      if (createStep) {
        createStep.hidden =
          false;

        createStep.classList.add(
          'multiverse-creator__step--active'
        );
      }

      if (resultStep) {
        resultStep.hidden =
          true;

        resultStep.classList.remove(
          'multiverse-creator__step--active'
        );
      }
    }


    function showResultStep() {
      if (createStep) {
        createStep.hidden =
          true;

        createStep.classList.remove(
          'multiverse-creator__step--active'
        );
      }

      if (resultStep) {
        resultStep.hidden =
          false;

        resultStep.classList.add(
          'multiverse-creator__step--active'
        );
      }

      window.scrollTo({
        top:
          section.getBoundingClientRect()
            .top +
          window.scrollY -
          20,
        behavior:
          'smooth'
      });
    }


    function showMessage(
      text,
      state = ''
    ) {
      if (!message) {
        return;
      }

      message.textContent =
        text || '';

      if (state) {
        message.dataset.state =
          state;
      } else {
        delete message.dataset.state;
      }
    }


    function setGenerating(
      active
    ) {
      generationInFlight =
        active;

      if (loadingOverlay) {
        loadingOverlay.hidden =
          !active;
      }

      if (miniLoading) {
        miniLoading.hidden =
          !active;
      }

      if (createButton) {
        createButton.disabled =
          active ||
          (
            quotaState &&
            quotaState.can_generate ===
            false
          );
      }

      if (createButtonText) {
        createButtonText.textContent =
          active
            ? 'Creating Your Multiverse…'
            : 'Create Your Multiverse';
      }
    }


    function populateResult() {
      const artUrl =
        generatedObjectUrl ||
        generatedArtworkUrl;

      if (
        resultImage &&
        artUrl
      ) {
        resultImage.src =
          artUrl;

        resultImage
          .closest(
            '.multiverse-creator__result-main'
          )
          ?.classList.remove(
            'is-original'
          );
      }

      if (
        resultVibe
      ) {
        resultVibe.textContent =
          currentVibe ||
          'Romantic';
      }

      if (
        resultGenerationId
      ) {
        resultGenerationId.textContent =
          generationId
            ? generationId.slice(
                0,
                8
              )
            : '—';

        resultGenerationId.title =
          generationId ||
          '';
      }

      resultViewButtons.forEach(
        button => {
          button.classList.toggle(
            'is-active',
            button.dataset.mvResultView ===
              'art'
          );
        }
      );
    }


    function setResultView(
      view
    ) {
      if (!resultImage) {
        return;
      }

      const main =
        resultImage.closest(
          '.multiverse-creator__result-main'
        );

      if (
        view ===
        'original' &&
        originalObjectUrl
      ) {
        resultImage.src =
          originalObjectUrl;

        main?.classList.add(
          'is-original'
        );
      } else {
        resultImage.src =
          generatedObjectUrl ||
          generatedArtworkUrl;

        main?.classList.remove(
          'is-original'
        );
      }

      resultViewButtons.forEach(
        button => {
          button.classList.toggle(
            'is-active',
            button.dataset.mvResultView ===
              view
          );
        }
      );
    }



    /* -----------------------------
       STEP 4 PRODUCT COMMERCE
    ------------------------------ */

    function formatMoney(
      cents
    ) {
      try {
        return new Intl.NumberFormat(
          document.documentElement.lang ||
          'en',
          {
            style:
              'currency',
            currency:
              shopCurrency,
            minimumFractionDigits:
              2
          }
        ).format(
          Number(cents || 0) / 100
        );
      } catch (_) {
        return `$${(
          Number(cents || 0) / 100
        ).toFixed(2)}`;
      }
    }


    function parseVariants(
      card
    ) {
      const script =
        card.querySelector(
          '[data-mv-variants]'
        );

      if (!script) {
        return [];
      }

      try {
        return JSON.parse(
          script.textContent ||
          '[]'
        );
      } catch (error) {
        console.warn(
          '[Multiverse Creator] Could not parse product variants:',
          error
        );
        return [];
      }
    }


    function selectedOptionValues(
      card
    ) {
      return Array.from(
        card.querySelectorAll(
          '[data-mv-option]'
        )
      )
        .sort(
          (a, b) =>
            Number(
              a.dataset.mvOptionIndex ||
              0
            ) -
            Number(
              b.dataset.mvOptionIndex ||
              0
            )
        )
        .map(
          select =>
            select.value
        );
    }


    function getSelectedVariant(
      card
    ) {
      const variants =
        parseVariants(
          card
        );

      if (!variants.length) {
        return null;
      }

      const optionValues =
        selectedOptionValues(
          card
        );

      if (!optionValues.length) {
        return (
          variants.find(
            variant =>
              variant.available
          ) ||
          variants[0] ||
          null
        );
      }

      return (
        variants.find(
          variant =>
            Array.isArray(
              variant.options
            ) &&
            variant.options.length ===
              optionValues.length &&
            variant.options.every(
              (
                value,
                index
              ) =>
                String(value) ===
                String(
                  optionValues[index]
                )
            )
        ) ||
        null
      );
    }


    function updateProductCard(
      card
    ) {
      const variant =
        getSelectedVariant(
          card
        );

      const price =
        card.querySelector(
          '[data-mv-product-price]'
        );

      const availability =
        card.querySelector(
          '[data-mv-product-availability]'
        );

      const checkbox =
        card.querySelector(
          '[data-mv-product-select]'
        );

      if (!variant) {
        card.dataset.mvVariantId =
          '';

        if (availability) {
          availability.textContent =
            'Combination unavailable';

          availability.classList.add(
            'is-unavailable'
          );
        }

        if (checkbox) {
          checkbox.checked =
            false;

          checkbox.disabled =
            true;
        }

        refreshSelectedSummary();
        return;
      }

      card.dataset.mvVariantId =
        String(
          variant.id
        );

      card.dataset.mvVariantPrice =
        String(
          variant.price ||
          0
        );

      if (price) {
        price.textContent =
          formatMoney(
            variant.price ||
            0
          );
      }

      if (availability) {
        availability.textContent =
          variant.available
            ? 'Made with your Multiverse artwork'
            : 'Currently unavailable';

        availability.classList.toggle(
          'is-unavailable',
          !variant.available
        );
      }

      if (checkbox) {
        checkbox.disabled =
          !variant.available;

        if (!variant.available) {
          checkbox.checked =
            false;
        }
      }

      refreshProductCheckLabel(
        card
      );

      refreshSelectedSummary();
    }


    function refreshProductCheckLabel(
      card
    ) {
      const checkbox =
        card.querySelector(
          '[data-mv-product-select]'
        );

      const label =
        card.querySelector(
          '[data-mv-product-check-label]'
        );

      if (
        checkbox &&
        label
      ) {
        label.textContent =
          checkbox.checked
            ? 'Added'
            : 'Add';
      }
    }


    function productMockupForCard(
      card
    ) {
      const sceneKey =
        (
          card?.dataset?.mvSceneKey ||
          ''
        ).trim();

      if (!sceneKey) {
        return null;
      }

      return (
        generatedProductMockups.find(
          item => item.scene_key === sceneKey
        ) ||
        null
      );
    }


    function renderProductArtwork() {
      const artwork = generatedObjectUrl || generatedArtworkUrl || '';
      productCards.forEach(card => {
        const area = card.querySelector('.multiverse-creator__product-media');
        if (!area) return;
        const overlay = card.querySelector('[data-mv-product-art]');
        let base = card.querySelector('.multiverse-creator__mockup-base');
        const scene = productMockupForCard(card);
        const placeholder = area.querySelector('.multiverse-creator__product-placeholder');
        if (scene?.preview_url) {
          if (!base) {
            base = document.createElement('img');
            base.className = 'multiverse-creator__mockup-base';
            area.prepend(base);
            base.dataset.mvCreated = 'true';
          }
          if (base.dataset.mvOriginalSrc === undefined) {
            base.dataset.mvOriginalSrc = base.getAttribute('src') || '';
            base.dataset.mvOriginalSrcset = base.getAttribute('srcset') || '';
            base.dataset.mvOriginalSizes = base.getAttribute('sizes') || '';
          }
          base.src = scene.preview_url;
          base.removeAttribute('srcset');
          base.removeAttribute('sizes');
          base.alt = scene.title || 'Your personalized product';
          base.hidden = false;
          if (placeholder) placeholder.hidden = true;
          if (overlay) overlay.hidden = true;
        } else {
          if (base && base.dataset.mvOriginalSrc !== undefined) {
            const original = base.dataset.mvOriginalSrc;
            if (base.dataset.mvCreated === 'true' && !original) {
              base.remove();
            } else {
              if (original) base.src = original;
              else base.removeAttribute('src');
              if (base.dataset.mvOriginalSrcset) base.srcset = base.dataset.mvOriginalSrcset;
              if (base.dataset.mvOriginalSizes) base.sizes = base.dataset.mvOriginalSizes;
            }
          }
          if (placeholder) placeholder.hidden = false;
          if (overlay) {
            if (artwork) {overlay.src = artwork; overlay.hidden = false;}
            else {overlay.removeAttribute('src');overlay.hidden = true;}
          }
        }
      });
    }


    function refreshSelectedSummary() {
      let count =
        0;

      let totalCents =
        0;

      productCards.forEach(
        card => {
          const checkbox =
            card.querySelector(
              '[data-mv-product-select]'
            );

          refreshProductCheckLabel(
            card
          );

          if (
            !checkbox ||
            !checkbox.checked ||
            checkbox.disabled
          ) {
            return;
          }

          const variant =
            getSelectedVariant(
              card
            );

          if (
            !variant ||
            !variant.available
          ) {
            return;
          }

          count += 1;

          totalCents +=
            Number(
              variant.price ||
              0
            );
        }
      );

      if (selectedCount) {
        selectedCount.textContent =
          count === 1
            ? '1 product selected'
            : `${count} products selected`;
      }

      if (selectedTotal) {
        selectedTotal.textContent =
          formatMoney(
            totalCents
          );
      }

      if (addSelectedButton) {
        addSelectedButton.disabled =
          (
            count === 0 ||
            !generationId ||
            !generatedArtworkUrl
          );
      }

      updateBundleTotals();
    }


    function cardByHandle(handle) {
      return productCards.find(
        card => card.dataset.mvProductHandle === handle
      ) || null;
    }


    function bundleHandles(bundle) {
      return (bundle.dataset.mvBundleHandles || '')
        .split('|')
        .map(value => value.trim())
        .filter(Boolean);
    }


    function bundleTotalCents(bundle) {
      return bundleHandles(bundle).reduce(
        (total, handle) => {
          const card = cardByHandle(handle);

          if (!card) {
            return total;
          }

          const variant = getSelectedVariant(card);

          if (!variant || !variant.available) {
            return total;
          }

          return total + Number(variant.price || 0);
        },
        0
      );
    }


    function updateBundleTotals() {
      bundleCards.forEach(
        bundle => {
          const total = bundle.querySelector(
            '[data-mv-bundle-total]'
          );

          if (total) {
            total.textContent = formatMoney(
              bundleTotalCents(bundle)
            );
          }

          const handles = bundleHandles(bundle);

          const active =
            handles.length > 0 &&
            handles.every(
              handle => {
                const checkbox =
                  cardByHandle(handle)?.querySelector(
                    '[data-mv-product-select]'
                  );

                return Boolean(checkbox?.checked);
              }
            );

          bundle.classList.toggle(
            'is-active',
            active
          );
        }
      );
    }


    function applyBundle(bundle) {
      const handles = bundleHandles(bundle);

      if (!handles.length) {
        showCartMessage(
          'Configure products inside this collection in the Theme Editor.',
          'error'
        );
        return;
      }

      productCards.forEach(
        card => {
          const checkbox = card.querySelector(
            '[data-mv-product-select]'
          );

          if (checkbox && !checkbox.disabled) {
            checkbox.checked = handles.includes(
              card.dataset.mvProductHandle || ''
            );

            refreshProductCheckLabel(card);
          }
        }
      );

      refreshSelectedSummary();
      updateBundleTotals();
      persistProductSelections();

      productsArea
        ?.querySelector('.multiverse-creator__product-grid')
        ?.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
    }


    async function persistProductSelections() {
      try {
        const saved =
          await getSession(
            storageKey
          );

        if (!saved) {
          return;
        }

        const productSelections =
          productCards.map(
            card => {
              const checkbox =
                card.querySelector(
                  '[data-mv-product-select]'
                );

              return {
                blockId:
                  card.dataset.mvBlockId ||
                  '',
                selected:
                  Boolean(
                    checkbox?.checked
                  ),
                variantId:
                  card.dataset.mvVariantId ||
                  '',
                optionValues:
                  selectedOptionValues(
                    card
                  )
              };
            }
          );

        await saveSession({
          ...saved,
          productSelections,
          savedAt:
            Date.now()
        });
      } catch (error) {
        console.warn(
          '[Multiverse Creator] Could not save product selections:',
          error
        );
      }
    }


    async function restoreProductSelections(
      saved
    ) {
      const selections =
        saved?.productSelections;

      if (
        !Array.isArray(
          selections
        )
      ) {
        productCards.forEach(
          updateProductCard
        );

        refreshSelectedSummary();
        return;
      }

      productCards.forEach(
        card => {
          const savedCard =
            selections.find(
              item =>
                item.blockId ===
                card.dataset.mvBlockId
            );

          if (!savedCard) {
            updateProductCard(
              card
            );
            return;
          }

          const selects =
            Array.from(
              card.querySelectorAll(
                '[data-mv-option]'
              )
            )
              .sort(
                (a, b) =>
                  Number(
                    a.dataset.mvOptionIndex ||
                    0
                  ) -
                  Number(
                    b.dataset.mvOptionIndex ||
                    0
                  )
              );

          if (
            Array.isArray(
              savedCard.optionValues
            )
          ) {
            selects.forEach(
              (
                select,
                index
              ) => {
                const value =
                  savedCard.optionValues[
                    index
                  ];

                if (
                  value != null
                ) {
                  select.value =
                    value;
                }
              }
            );
          }

          updateProductCard(
            card
          );

          const checkbox =
            card.querySelector(
              '[data-mv-product-select]'
            );

          if (
            checkbox &&
            !checkbox.disabled
          ) {
            checkbox.checked =
              Boolean(
                savedCard.selected
              );
          }

          refreshProductCheckLabel(
            card
          );
        }
      );

      refreshSelectedSummary();
    }


    function showCartMessage(
      text,
      state = ''
    ) {
      if (!cartMessage) {
        return;
      }

      cartMessage.textContent =
        text || '';

      if (state) {
        cartMessage.dataset.state =
          state;
      } else {
        delete cartMessage.dataset.state;
      }
    }


    async function parseShopifyResponse(
      response
    ) {
      try {
        return await response.json();
      } catch (_) {
        return {};
      }
    }


    function supplierVariantMap(
      card
    ) {
      const script =
        card.querySelector(
          '[data-mv-supplier-variant-map]'
        );

      if (!script) {
        return {};
      }

      try {
        const parsed =
          JSON.parse(
            script.textContent ||
            '{}'
          );

        return (
          parsed &&
          typeof parsed ===
            'object'
        )
          ? parsed
          : {};
      } catch (error) {
        console.warn(
          '[Multiverse Creator] Invalid supplier variant map:',
          error
        );

        return {};
      }
    }


    function productDescriptor(card, variant) {
      const optionNames =
        Array.from(
          card.querySelectorAll('[data-mv-option]')
        ).map(
          select =>
            select
              .closest('label')
              ?.querySelector('span')
              ?.textContent
              ?.trim() || ''
        );

      const values = selectedOptionValues(card);
      const options = {};

      values.forEach(
        (value, index) => {
          options[
            optionNames[index] || `Option ${index + 1}`
          ] = value;
        }
      );

      return {
        client_job_key:
          `${card.dataset.mvBlockId || 'product'}-${variant.id}`,
        product_handle:
          card.dataset.mvProductHandle || '',
        product_title:
          card.dataset.mvProductTitle || '',
        scene_key:
          card.dataset.mvSceneKey || '',
        variant_id:
          String(variant.id),
        variant_title:
          variant.title || '',
        options,
        mockup_url:
          card.dataset.mvMockupUrl || '',
        artwork_placement: {
          left: Number(card.dataset.mvArtLeft || 50),
          top: Number(card.dataset.mvArtTop || 50),
          width: Number(card.dataset.mvArtWidth || 48),
          rotation: Number(card.dataset.mvArtRotation || 0),
          opacity: Number(card.dataset.mvArtOpacity || 100)
        },
        print_spec: {
          width: Number(card.dataset.mvPrintWidth || 3000),
          height: Number(card.dataset.mvPrintHeight || 3000),
          margin_percent: Number(card.dataset.mvPrintMargin || 5)
        },
        fulfillment_provider:
          card.dataset.mvFulfillmentProvider ||
          'manual',
        supplier_catalog_id:
          card.dataset.mvSupplierCatalogId ||
          '',
        supplier_provider_id:
          card.dataset.mvSupplierProviderId ||
          '',
        supplier_variant_id:
          String(
            supplierVariantMap(
              card
            )[
              String(
                variant.id
              )
            ] ||
            ''
          )
      };
    }


    async function prepareProductionJobs(descriptors) {
      if (!apiBaseUrl) {
        throw new Error(
          'Multiverse API is not configured.'
        );
      }

      const response = await fetch(
        `${apiBaseUrl}${productionPath}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          credentials: 'omit',
          body: JSON.stringify({
            generation_id: generationId,
            products: descriptors
          })
        }
      );

      let data = null;

      try {
        data = await response.json();
      } catch (_) {}

      if (!response.ok) {
        throw new Error(
          data?.detail?.message ||
          data?.detail ||
          data?.message ||
          'Unable to prepare your personalized print files.'
        );
      }

      if (
        !Array.isArray(data?.jobs) ||
        data.jobs.length !== descriptors.length
      ) {
        throw new Error(
          'The production server returned an incomplete job list.'
        );
      }

      return data.jobs;
    }


    async function addSelectedProductsToCart() {
      if (!generationId || !generatedArtworkUrl) {
        showCartMessage(
          'Your Multiverse artwork is missing. Generate your characters again.',
          'error'
        );
        return;
      }

      const selectedEntries = [];

      productCards.forEach(
        card => {
          const checkbox = card.querySelector(
            '[data-mv-product-select]'
          );

          if (
            !checkbox ||
            !checkbox.checked ||
            checkbox.disabled
          ) {
            return;
          }

          const variant = getSelectedVariant(card);

          if (!variant || !variant.available) {
            return;
          }

          selectedEntries.push({
            card,
            variant,
            descriptor: productDescriptor(card, variant)
          });
        }
      );

      if (!selectedEntries.length) {
        showCartMessage(
          'Choose at least one product first.',
          'error'
        );
        return;
      }

      if (addSelectedButton) {
        addSelectedButton.disabled = true;
      }

      if (addSelectedText) {
        addSelectedText.textContent =
          'Preparing Your Products…';
      }

      selectionBar?.classList.add(
        'is-preparing'
      );

      selectedEntries.forEach(
        entry => {
          entry.card.classList.add(
            'is-loading'
          );
        }
      );

      showCartMessage(
        'Preparing print-ready files for your selected products…'
      );

      try {
        const jobs = await prepareProductionJobs(
          selectedEntries.map(
            entry => entry.descriptor
          )
        );

        const jobsByKey = new Map(
          jobs.map(
            job => [
              job.client_job_key,
              job
            ]
          )
        );

        const story =
          form?.querySelector(
            '[name="multiverse_story"]'
          )?.value.trim() || '';

        const world =
          form?.querySelector(
            '[name="multiverse_world"]'
          )?.value.trim() || '';

        const selectedItems = selectedEntries.map(
          entry => {
            const job = jobsByKey.get(
              entry.descriptor.client_job_key
            );

            if (!job) {
              throw new Error(
                `Production file missing for ${entry.card.dataset.mvProductTitle || 'a selected product'}.`
              );
            }

            const properties = {
              'Personalization':
                'Custom Multiverse Artwork',
              '_generation_id':
                generationId,
              '_artwork_url':
                generatedArtworkUrl,
              '_multiverse_vibe':
                currentVibe || 'Romantic',
              '_multiverse_product':
                entry.card.dataset.mvProductTitle || '',
              '_multiverse_source':
                'character_creator',
              '_production_job_id':
                job.production_job_id,
              '_print_file_url':
                job.print_file_url,
              '_fulfillment_provider':
                job.fulfillment_provider ||
                'manual',
              '_supplier_variant_id':
                job.supplier_variant_id ||
                ''
            };

            if (job.preview_url) {
              properties._multiverse_preview_url =
                job.preview_url;
            }

            if (story) {
              properties._multiverse_story =
                story;
            }

            if (world) {
              properties._multiverse_world =
                world;
            }

            return {
              id: Number(entry.variant.id),
              quantity: 1,
              properties
            };
          }
        );

        if (addSelectedText) {
          addSelectedText.textContent =
            'Adding Your Collection…';
        }

        showCartMessage(
          'Print files ready. Adding your personalized products to cart…'
        );

        const cartEventLines =
          selectedItems.map(
            item => ({
              merchandiseId: String(item.id),
              quantity: Number(item.quantity)
            })
          );

        let CartLinesUpdateEvent = null;
        let deferredCartUpdate = null;

        try {
          const shopifyEvents = await import(
            '@shopify/events'
          );

          CartLinesUpdateEvent =
            shopifyEvents.CartLinesUpdateEvent;

          deferredCartUpdate =
            CartLinesUpdateEvent.createPromise();

          document.dispatchEvent(
            new CartLinesUpdateEvent({
              action: 'add',
              context: 'product',
              lines: cartEventLines,
              promise: deferredCartUpdate.promise
            })
          );
        } catch (eventImportError) {
          console.warn(
            '[Multiverse Creator] Shopify cart event module unavailable:',
            eventImportError
          );
        }

        const cartSectionIds =
          Array.from(
            document.querySelectorAll(
              'cart-items-component[data-section-id]'
            )
          )
            .map(
              component => component.dataset.sectionId
            )
            .filter(Boolean);

        if (
          !cartSectionIds.includes(
            'cart-drawer-section'
          )
        ) {
          cartSectionIds.push(
            'cart-drawer-section'
          );
        }

        const response = await fetch(
          cartAddUrl,
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            credentials: 'same-origin',
            body: JSON.stringify({
              items: selectedItems,
              sections: cartSectionIds.join(','),
              sections_url: window.location.pathname
            })
          }
        );

        const result =
          await parseShopifyResponse(response);

        if (!response.ok) {
          throw new Error(
            result?.description ||
            result?.message ||
            `Unable to add the selected products (${response.status}).`
          );
        }

        let ajaxCart = null;

        try {
          const cartResponse = await fetch(
            `${shopRoot}cart.js`,
            {
              headers: {
                'Accept': 'application/json'
              },
              credentials: 'same-origin',
              cache: 'no-store'
            }
          );

          if (cartResponse.ok) {
            ajaxCart =
              await cartResponse.json();
          }
        } catch (cartRefreshError) {
          console.warn(
            '[Multiverse Creator] Could not fetch refreshed cart:',
            cartRefreshError
          );
        }

        if (
          deferredCartUpdate &&
          CartLinesUpdateEvent
        ) {
          deferredCartUpdate.resolve({
            cart:
              ajaxCart
                ? CartLinesUpdateEvent
                    .createCartFromAjaxResponse(
                      ajaxCart
                    )
                : undefined,
            detail: {
              items:
                ajaxCart?.items ||
                result?.items ||
                [],
              source:
                'multiverse-character-creator',
              sourceId:
                section.id ||
                'multiverse-character-creator',
              itemCount:
                cartEventLines.reduce(
                  (total, line) =>
                    total +
                    Number(line.quantity || 0),
                  0
                ),
              sections:
                result?.sections || {},
              didError: false
            }
          });

          await new Promise(
            resolve =>
              requestAnimationFrame(
                () =>
                  requestAnimationFrame(resolve)
              )
          );
        }

        showCartMessage(
          selectedItems.length === 1
            ? 'Your personalized product and print file are ready in cart ✓'
            : `${selectedItems.length} personalized products and print files are ready in cart ✓`,
          'success'
        );

        if (window.Shopify?.actions?.openCart) {
          window.Shopify.actions.openCart();
        } else {
          const drawer =
            document.querySelector(
              'theme-drawer#cart-drawer'
            );

          if (
            drawer &&
            typeof drawer.open === 'function'
          ) {
            drawer.open();
          } else {
            document
              .querySelector(
                '.header-actions__cart-icon, [href$="/cart"]'
              )
              ?.click();
          }
        }
      } catch (error) {
        showCartMessage(
          error?.message ||
          'Unable to prepare or add your selected products. Please try again.',
          'error'
        );
      } finally {
        selectedEntries.forEach(
          entry => {
            entry.card.classList.remove(
              'is-loading'
            );
          }
        );

        selectionBar?.classList.remove(
          'is-preparing'
        );

        if (addSelectedText) {
          addSelectedText.textContent =
            'Add Selected to Cart';
        }

        refreshSelectedSummary();
      }
    }


    chooseProductsButton?.addEventListener(
      'click',
      () => {
        productsArea?.scrollIntoView({
          behavior:
            'smooth',
          block:
            'start'
        });
      }
    );


    bundleCards.forEach(
      bundle => {
        bundle.addEventListener(
          'click',
          () => {
            applyBundle(bundle);
          }
        );
      }
    );


    productCards.forEach(
      card => {
        card
          .querySelectorAll(
            '[data-mv-option]'
          )
          .forEach(
            select => {
              select.addEventListener(
                'change',
                () => {
                  updateProductCard(
                    card
                  );

                  persistProductSelections();
                }
              );
            }
          );

        const checkbox =
          card.querySelector(
            '[data-mv-product-select]'
          );

        checkbox?.addEventListener(
          'change',
          () => {
            refreshProductCheckLabel(
              card
            );

            refreshSelectedSummary();

            persistProductSelections();
          }
        );

        updateProductCard(
          card
        );
      }
    );


    addSelectedButton?.addEventListener(
      'click',
      addSelectedProductsToCart
    );


    /* -----------------------------
       QUOTA
    ------------------------------ */

    function renderQuota(
      quota
    ) {
      if (
        !quota ||
        !quotaBox
      ) {
        return;
      }

      quotaState =
        quota;

      quotaBox.hidden =
        false;

      if (quota.unlimited === true) {
        if (quotaTitle) {
          quotaTitle.textContent = 'Unlimited generations (owner)';
        }
        if (quotaSubtitle) {
          quotaSubtitle.textContent = 'Daily generation limit disabled for your IP.';
        }
        if (quotaProgress) {
          quotaProgress.style.width = '100%';
        }
        if (createButton && !generationInFlight) {
          createButton.disabled = false;
        }
        showMessage('', '');
        return;
      }

      const limit =
        Number(
          quota.limit ||
          5
        );

      const remaining =
        Math.max(
          0,
          Number(
            quota.remaining ||
            0
          )
        );

      const used =
        Math.max(
          0,
          Number(
            quota.used ||
            0
          )
        );

      if (quotaTitle) {
        quotaTitle.textContent =
          remaining === 1
            ? '1 free generation left today'
            : `${remaining} of ${limit} free generations left today`;
      }

      if (quotaSubtitle) {
        quotaSubtitle.textContent =
          remaining > 0
            ? 'Your free allowance resets automatically each day.'
            : 'Daily free generation limit reached.';
      }

      if (quotaProgress) {
        const percent =
          limit > 0
            ? Math.min(
                100,
                Math.max(
                  0,
                  (
                    remaining /
                    limit
                  ) * 100
                )
              )
            : 0;

        quotaProgress.style.width =
          `${percent}%`;
      }

      if (
        createButton &&
        !generationInFlight
      ) {
        createButton.disabled =
          quota.can_generate ===
          false;
      }

      if (
        quota.can_generate ===
        false
      ) {
        showMessage(
          'Daily free generation limit reached. Please come back after the allowance resets.',
          'error'
        );
      }
    }


    async function refreshQuota() {
      if (!apiBaseUrl) {
        return;
      }

      const url =
        new URL(
          `${apiBaseUrl}${quotaPath}`
        );

      if (customerId) {
        url.searchParams.set(
          'customer_id',
          customerId
        );
      }

      try {
        const response =
          await fetch(
            url.toString(),
            {
              headers: {
                Accept:
                  'application/json'
              },
              credentials:
                'omit',
              cache:
                'no-store'
            }
          );

        if (!response.ok) {
          return;
        }

        const quota =
          await response.json();

        renderQuota(
          quota
        );
      } catch (error) {
        console.warn(
          '[Multiverse Creator] Unable to load quota:',
          error
        );
      }
    }


    /* -----------------------------
       PERSISTENCE
    ------------------------------ */

    async function persistCompletedSession() {
      try {
        await saveSession({
          key:
            storageKey,
          status:
            'completed',
          generationId,
          artworkUrl:
            generatedArtworkUrl,
          artworkBlob:
            generatedArtworkBlob,
          originalPhotoBlob,
          originalPhotoName,
          vibe:
            currentVibe,
          story:
            form?.querySelector(
              '[name="multiverse_story"]'
            )?.value ||
            '',
          world:
            form?.querySelector(
              '[name="multiverse_world"]'
            )?.value ||
            '',
          productMockups:
            generatedProductMockups,
          productSelections:
            productCards.map(
              card => {
                const checkbox =
                  card.querySelector(
                    '[data-mv-product-select]'
                  );

                return {
                  blockId:
                    card.dataset.mvBlockId ||
                    '',
                  selected:
                    Boolean(
                      checkbox?.checked
                    ),
                  variantId:
                    card.dataset.mvVariantId ||
                    '',
                  optionValues:
                    selectedOptionValues(
                      card
                    )
                };
              }
            ),
          savedAt:
            Date.now()
        });
      } catch (error) {
        console.warn(
          '[Multiverse Creator] Unable to persist completed session:',
          error
        );
      }
    }


    async function restoreSession() {
      let saved =
        null;

      try {
        saved =
          await getSession(
            storageKey
          );
      } catch (error) {
        console.warn(
          '[Multiverse Creator] Unable to restore session:',
          error
        );
        return;
      }

      if (
        !saved ||
        saved.status !==
          'completed'
      ) {
        return;
      }

      generationId =
        saved.generationId ||
        '';

      generatedArtworkUrl =
        saved.artworkUrl ||
        '';

      currentVibe =
        saved.vibe ||
        'Romantic';

      generatedProductMockups =
        Array.isArray(
          saved.productMockups
        )
          ? saved.productMockups
          : [];

      if (
        saved.originalPhotoBlob
      ) {
        setOriginalBlob(
          saved.originalPhotoBlob,
          saved.originalPhotoName ||
          'saved-photo.png'
        );
      }

      if (
        saved.artworkBlob
      ) {
        setGeneratedBlob(
          saved.artworkBlob,
          saved.artworkUrl ||
          ''
        );
      } else if (
        saved.artworkUrl
      ) {
        setGeneratedBlob(
          null,
          saved.artworkUrl
        );
      }

      const vibeInput =
        form?.querySelector(
          `input[name="multiverse_vibe"][value="${cssEscape(currentVibe)}"]`
        );

      if (vibeInput) {
        vibeInput.checked =
          true;
      }

      const storyField =
        form?.querySelector(
          '[name="multiverse_story"]'
        );

      if (storyField) {
        storyField.value =
          saved.story ||
          '';
      }

      const worldField =
        form?.querySelector(
          '[name="multiverse_world"]'
        );

      if (worldField) {
        worldField.value =
          saved.world ||
          '';
      }

      await restoreProductSelections(
        saved
      );

      renderProductArtwork();

      if (
        generatedObjectUrl ||
        generatedArtworkUrl
      ) {
        populateResult();
        showResultStep();
      }
    }


    /* -----------------------------
       PHOTO
    ------------------------------ */

    if (
      photoInput
    ) {
      photoInput.addEventListener(
        'change',
        () => {
          const file =
            photoInput.files?.[0];

          if (!file) {
            return;
          }

          if (
            !isSupportedImage(
              file
            )
          ) {
            photoInput.value =
              '';

            showMessage(
              'Please upload a JPG, PNG, WEBP, HEIC or HEIF image.',
              'error'
            );

            return;
          }

          if (
            file.size >
            MAX_FILE_BYTES
          ) {
            photoInput.value =
              '';

            showMessage(
              'Please choose an image smaller than 12MB.',
              'error'
            );

            return;
          }

          setOriginalBlob(
            file,
            file.name
          );

          if (
            generatedPreview
          ) {
            generatedPreview.hidden =
              true;
          }

          if (
            generatedThumb
          ) {
            generatedThumb.hidden =
              true;
          }

          showMessage(
            'Photo added ✓',
            'success'
          );
        }
      );
    }


    /* -----------------------------
       GENERATE
    ------------------------------ */

    function absoluteApiUrl(url) {
      if (!url) return '';
      try { return new URL(url, `${apiBaseUrl}/`).href; }
      catch (_) {return url;}
    }

    function setPendingJob(jobId) {
      currentJobId = jobId || '';
      try {
        if (jobId) localStorage.setItem(pendingJobKey, jobId);
        else localStorage.removeItem(pendingJobKey);
      } catch (_) {}
    }

    function renderJobProgress(data) {
      const completed = Number(data?.progress?.completed || 0);
      const total = Number(data?.progress?.total || 9);
      const label = data?.current_scene || 'Creating your Multiverse…';
      if (jobProgressText) jobProgressText.textContent = label;
      if (jobProgressCount) jobProgressCount.textContent = `${completed}/${total}`;
      if (jobProgressBar) jobProgressBar.style.width = `${Math.min(100, completed / Math.max(1,total) * 100)}%`;
      if (jobProgressBox) jobProgressBox.hidden = Boolean(data?.done && data?.status === 'completed');
      if (jobProgressSpinner) jobProgressSpinner.hidden = Boolean(data?.done);
      if (!hasShownMaster) showMessage(`${label} (${completed}/${total})`);
    }

    async function applyJobStatus(data) {
      const newArt = absoluteApiUrl(data?.artwork_url || '');
      if (hasShownMaster && newArt && generatedArtworkUrl !== newArt) {
        generatedArtworkUrl = newArt;
        await persistCompletedSession();
      }
      if (!hasShownMaster && newArt && data.generation_id) {
        generationId = data.generation_id;
        generatedArtworkUrl = newArt;
        let blob = null;
        try {
          const res = await fetch(newArt, {cache: 'no-store', credentials:'omit'});
          if (res.ok) blob = await res.blob();
        } catch (error) {console.warn('[Multiverse] Could not cache master image',error);}
        setGeneratedBlob(blob, newArt);
        populateResult();
        hasShownMaster = true;
        await persistCompletedSession();
        showResultStep();
      }
      const previews = Array.isArray(data?.product_mockups)
        ? data.product_mockups.map(item => ({...item, preview_url: absoluteApiUrl(item.preview_url)}))
        : [];
      if (previews.length !== lastPreviewCount) {
        generatedProductMockups = previews;
        lastPreviewCount = previews.length;
        renderProductArtwork();
        refreshSelectedSummary();
        if (hasShownMaster) await persistCompletedSession();
      }
      renderJobProgress(data);
      if (data?.done) {
        setPendingJob('');
        setGenerating(false);
        if (data.status === 'failed') {
          if (jobProgressBox) jobProgressBox.hidden = true;
          showMessage(data.error || 'Generation failed. Please try again.', 'error');
          return;
        }
        if (jobProgressBox) {
          jobProgressBox.hidden = data.status === 'completed';
          if (data.status === 'completed_with_errors' && jobProgressText) {
            jobProgressText.textContent = 'Some product previews were unavailable. You can still view your characters.';
          }
        }
        await refreshQuota();
      }
    }

    async function pollJob(jobId) {
      if (!jobId || !apiBaseUrl) return;
      const sequence = ++pollSequence;
      setPendingJob(jobId);
      generationInFlight = true;
      if (!hasShownMaster) setGenerating(true);
      let errors = 0;
      while (sequence === pollSequence) {
        try {
          const controller = new AbortController();
          const timeout = setTimeout(() => controller.abort(), 20000);
          let response;
          try {
            response = await fetch(`${apiBaseUrl}/api/multiverse/jobs/${encodeURIComponent(jobId)}`,
              {cache:'no-store',credentials:'omit',signal:controller.signal});
          } finally {clearTimeout(timeout);}
          if (!response.ok) throw new Error(`Job status unavailable (HTTP ${response.status})`);
          const data = await response.json();
          errors = 0;
          await applyJobStatus(data);
          if (data.done) return;
          await new Promise(resolve => setTimeout(resolve, 4500));
        } catch (error) {
          console.warn('[Multiverse] Job polling interrupted; reconnecting',error);
          errors += 1;
          if (!hasShownMaster) showMessage('Still working — reconnecting to the generator…');
          await new Promise(resolve => setTimeout(resolve, Math.min(20000, 3000 * errors)));
        }
      }
    }

    async function resumePendingJob() {
      let id = '';
      try {id = localStorage.getItem(pendingJobKey) || '';} catch (_) {}
      if (id && apiBaseUrl) await pollJob(id);
    }

    if (
      form
    ) {
      form.addEventListener(
        'submit',
        async event => {
          event.preventDefault();

          if (
            generationInFlight
          ) {
            return;
          }

          if (!apiBaseUrl) {
            showMessage(
              'New Multiverse API URL is not configured yet. Paste the port 8001 Cloudflare URL in the Theme Editor.',
              'error'
            );
            return;
          }

          const uploadedFile =
            photoInput?.files?.[0] ||
            null;

          const photo =
            uploadedFile ||
            originalPhotoBlob;

          if (!photo) {
            showMessage(
              'Please upload your photo first.',
              'error'
            );

            photoInput?.focus();
            return;
          }

          if (
            quotaState &&
            quotaState.can_generate ===
              false
          ) {
            showMessage(
              'Daily free generation limit reached.',
              'error'
            );
            return;
          }

          const vibe =
            form.querySelector(
              'input[name="multiverse_vibe"]:checked'
            )?.value ||
            'Romantic';

          const story =
            form.querySelector(
              '[name="multiverse_story"]'
            )?.value.trim() ||
            '';

          const world =
            form.querySelector(
              '[name="multiverse_world"]'
            )?.value.trim() ||
            '';

          const body =
            new FormData();

          body.append(
            'photo',
            photo,
            uploadedFile?.name ||
            originalPhotoName ||
            'multiverse-photo.png'
          );

          body.append(
            'vibe',
            vibe
          );

          body.append(
            'bond_special',
            story
          );

          body.append(
            'universe_details',
            world
          );

          if (customerId) {
            body.append(
              'customer_id',
              customerId
            );
          }

          currentVibe =
            vibe;

          setGenerating(
            true
          );

          showMessage(
            'Creating your Multiverse. This can take a little while ✨'
          );

          try {
            const controller = new AbortController();
            const timeout = setTimeout(() => controller.abort(), 25000);
            let response;
            try {
              response = await fetch(`${apiBaseUrl}${generatePath}`, {
                method: 'POST', body, signal: controller.signal,
                credentials: 'omit'
              });
            } finally {clearTimeout(timeout);}
            let data = null;
            try {data = await response.json();} catch (_) {}
            if (!response.ok) {
              const detail = data?.detail;
              if (response.status === 429 && detail?.quota) renderQuota(detail.quota);
              throw new Error(typeof detail === 'string' ? detail : detail?.message || 'Could not start generation.');
            }
            if (!data?.job_id) throw new Error('The server did not return a job ID.');
            if (data.quota) renderQuota(data.quota);
            hasShownMaster = false;
            lastPreviewCount = -1;
            generatedProductMockups = [];
            setPendingJob(data.job_id);
            await pollJob(data.job_id);
          } catch (error) {
            setGenerating(false);
            showMessage(error?.message || 'Unable to start generation.', 'error');
          }
        }
      );
    }


    /* -----------------------------
       RESULT CONTROLS
    ------------------------------ */

    resultViewButtons.forEach(
      button => {
        button.addEventListener(
          'click',
          () => {
            setResultView(
              button.dataset.mvResultView ||
              'art'
            );
          }
        );
      }
    );


    backButton?.addEventListener(
      'click',
      () => {
        showCreateStep();
      }
    );


    generateAnotherButton?.addEventListener(
      'click',
      async () => {
        const shouldReset =
          window.confirm(
            'Generate a new Multiverse? Your current saved design on this page will be replaced.'
          );

        if (!shouldReset) {
          return;
        }

        try {
          await deleteSession(
            storageKey
          );
        } catch (error) {
          console.warn(
            '[Multiverse Creator] Unable to clear saved session:',
            error
          );
        }

        // A job already running on the server may still finish; stop following it.
        ++pollSequence;
        setPendingJob('');
        hasShownMaster = false;
        lastPreviewCount = -1;
        setGenerating(false);
        if (jobProgressBox) jobProgressBox.hidden = true;

        generationId =
          '';

        generatedArtworkBlob =
          null;

        generatedArtworkUrl =
          '';

        generatedProductMockups =
          [];

        revokeUrl(
          generatedObjectUrl
        );

        generatedObjectUrl =
          '';

        renderProductArtwork();
        refreshSelectedSummary();

        if (
          generatedPreview
        ) {
          generatedPreview.hidden =
            true;
        }

        if (
          generatedThumb
        ) {
          generatedThumb.hidden =
            true;
        }

        showCreateStep();

        showMessage(
          'Your photo and details are still here. Create another version when ready.'
        );
      }
    );


    /* -----------------------------
       INITIALIZE
    ------------------------------ */

    refreshQuota();

    restoreSession().then(() => {
      if (generationId) hasShownMaster = true;
      resumePendingJob();
    });


    window.addEventListener(
      'pagehide',
      () => {
        revokeUrl(
          originalObjectUrl
        );

        revokeUrl(
          generatedObjectUrl
        );
      },
      {
        once:
          true
      }
    );
  }


  function isSupportedImage(
    file
  ) {
    const type =
      (
        file?.type ||
        ''
      ).toLowerCase();

    return [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/heic',
      'image/heif'
    ].includes(
      type
    );
  }


  function cssEscape(
    value
  ) {
    if (
      window.CSS &&
      typeof window.CSS.escape ===
        'function'
    ) {
      return window.CSS.escape(
        value
      );
    }

    return String(
      value
    ).replace(
      /["\\]/g,
      '\\$&'
    );
  }


  if (
    document.readyState ===
    'loading'
  ) {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        initAll();
      }
    );
  } else {
    initAll();
  }


  document.addEventListener(
    'shopify:section:load',
    event => {
      initAll(
        event.target
      );
    }
  );
})();
